import { commissionPercentStringFromRate, rateFromPercentInput } from "@/lib/commissionRate";
import { z } from "zod";
import type { House, HouseAdminUpdate, HouseUpdate } from "@/types";

export type HouseHostDetailFormValues = {
  name: string;
  address: string;
  coordinates: { lat: number; lng: number } | null;
};

export type HouseAdminDetailFormValues = HouseHostDetailFormValues & {
  commission_percent: string;
  is_active: boolean;
};

export function buildHouseHostFormDefaults(
  house: House,
): HouseHostDetailFormValues {
  return {
    name: house.name,
    address: house.address || "",
    coordinates: house.coordinates ?? null,
  };
}

export function buildHouseAdminFormDefaults(
  house: House,
): HouseAdminDetailFormValues {
  return {
    ...buildHouseHostFormDefaults(house),
    commission_percent: commissionPercentStringFromRate(house.commission_rate),
    is_active: house.is_active,
  };
}

export function houseUpdateFromHostForm(
  values: HouseHostDetailFormValues,
): HouseUpdate {
  const address = values.address.trim();
  return {
    name: values.name.trim(),
    address: address || undefined,
    coordinates: values.coordinates,
  };
}

export function houseUpdateFromAdminForm(
  values: HouseAdminDetailFormValues,
): HouseAdminUpdate {
  const update = houseUpdateFromHostForm(values) as HouseAdminUpdate;
  update.is_active = values.is_active;

  const rate = rateFromPercentInput(values.commission_percent);
  if (rate !== null) {
    update.commission_rate = rate;
  }

  return update;
}

export interface HouseHostFormMessages {
  nameRequired: string;
}

export interface HouseAdminFormMessages extends HouseHostFormMessages {
  commissionRequired: string;
  commissionRange: string;
}

function locationFields(messages: HouseHostFormMessages) {
  return {
    name: z.string().min(1, messages.nameRequired),
    address: z.string(),
    coordinates: z
      .object({
        lat: z.number(),
        lng: z.number(),
      })
      .nullable(),
  };
}

export function buildHouseHostFormSchema(messages: HouseHostFormMessages) {
  return z.object(locationFields(messages));
}

export function buildHouseAdminFormSchema(messages: HouseAdminFormMessages) {
  return z.object({
    ...locationFields(messages),
    commission_percent: z
      .string()
      .min(1, messages.commissionRequired)
      .refine(
        (value) => rateFromPercentInput(value) !== null,
        messages.commissionRange,
      ),
    is_active: z.boolean(),
  });
}

/** A typed address the geocoder could not place: saving it needs a confirm. */
export function needsUnlocatedAddressConfirm(
  values: Pick<HouseHostDetailFormValues, "address" | "coordinates">,
): boolean {
  return Boolean(values.address.trim()) && !values.coordinates;
}
